import React, { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchUsers } from "../../redux/apis/normalUser/userListSlice";

const PatientsViewWithAccessControl = ({
  groupedByPatient = {},
  currentUserEmail,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { loading: authLoading, isAuthenticated } = useSelector(
    (state) => state.auth
  );

  const { users, loading: usersLoading } = useSelector(
    (state) => state.userList
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [professionFilter, setProfessionFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // 🔐 auth redirect
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [authLoading, isAuthenticated, navigate]);

  // 📡 fetch users
  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  // ============================
  // 🔥 SAFE USERS NORMALIZATION (CRITICAL FIX)
  // ============================
  const safeUsers = useMemo(() => {
    if (Array.isArray(users)) return users;
    if (Array.isArray(users?.users)) return users.users;
    if (Array.isArray(users?.data)) return users.data;
    if (users && typeof users === "object") return [users];
    return [];
  }, [users]);

  // ============================
  // 🧠 USER MAP (NO CRASH EVER)
  // ============================
  const userMap = useMemo(() => {
    const map = new Map();

    safeUsers.forEach((u) => {
      if (u?._id) map.set(u._id, u);
    });

    return map;
  }, [safeUsers]);

  // ============================
  // 🧠 NORMALIZE PATIENT GROUPS
  // ============================
  const safeGroups = useMemo(() => {
    const result = [];

    Object.values(groupedByPatient || {}).forEach((group) => {
      const patientId = group?.patient?.id || group?.patient?._id;

      if (!patientId) return;

      const user = userMap.get(patientId);

      result.push({
        patient: {
          _id: patientId,
          fullName: user?.name || group?.patient?.name || "Unknown",
          email: user?.email || group?.patient?.email || "",
          phone: user?.contact || group?.patient?.phone || "",
          profession: user?.role || "user",
        },
        appointments: Array.isArray(group?.appointments)
          ? group.appointments
          : [],
      });
    });

    return result;
  }, [groupedByPatient, userMap]);

  // ============================
  // 🔍 FILTER LOGIC
  // ============================
  const filteredPatients = safeGroups.filter(({ patient, appointments }) => {
    const search = searchTerm.toLowerCase();

    const matchesSearch =
      patient.fullName?.toLowerCase().includes(search) ||
      patient.email?.toLowerCase().includes(search) ||
      patient.phone?.toLowerCase().includes(search);

    const matchesProfession =
      !professionFilter || patient.profession === professionFilter;

    const matchesDate = appointments.some((appt) => {
      const d = new Date(appt.date);
      return (
        (!startDate || d >= new Date(startDate)) &&
        (!endDate || d <= new Date(endDate))
      );
    });

    return matchesSearch && matchesProfession && matchesDate;
  });

  // ============================
  // 🧠 UNIQUE PROFESSIONS
  // ============================
  const uniqueProfessions = [
    ...new Set(safeGroups.map((p) => p?.patient?.profession).filter(Boolean)),
  ];

  if (usersLoading) {
    return <p className="text-center p-6">Loading...</p>;
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">

      {/* FILTERS */}
      <div className="bg-white p-4 rounded shadow-md mb-6 flex flex-col md:flex-row gap-4">

        <input
          type="text"
          placeholder="Search patients..."
          className="border p-2 rounded w-full md:w-1/4"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="border p-2 rounded w-full md:w-1/4"
          value={professionFilter}
          onChange={(e) => setProfessionFilter(e.target.value)}
        >
          <option value="">All Professions</option>
          {uniqueProfessions.map((prof, idx) => (
            <option key={idx} value={prof}>
              {prof}
            </option>
          ))}
        </select>

        <input
          type="date"
          className="border p-2 rounded w-full md:w-1/4"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />

        <input
          type="date"
          className="border p-2 rounded w-full md:w-1/4"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
      </div>

      {/* PATIENT LIST */}
      {filteredPatients.length === 0 ? (
        <p className="text-center text-gray-500">
          No matching patients found.
        </p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">

          {filteredPatients.map(({ patient, appointments }) => (
            <div
              key={patient._id}
              className="bg-white p-4 rounded shadow border"
            >
              <h2 className="text-lg font-semibold text-blue-700">
                {patient.fullName}
              </h2>

              <p>Email: {patient.email}</p>
              <p>Phone: {patient.phone}</p>
              <p>Profession: {patient.profession}</p>

              <p className="text-blue-600 mt-2">
                Total: {appointments.length}
              </p>

              {appointments.map((appt, idx) => (
                <div key={idx} className="mt-2 border-t pt-2 text-sm">
                  <p>Date: {new Date(appt.date).toLocaleString()}</p>
                  <p>Status: {appt.status}</p>
                  <p>Notes: {appt.notes || "N/A"}</p>
                </div>
              ))}

            </div>
          ))}

        </div>
      )}
    </div>
  );
};

export default PatientsViewWithAccessControl;